import React, { useMemo, useEffect } from "react";
import { Grid, TextField, Button, Box, Select, MenuItem } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useState } from "react";
import dayjs from "dayjs";

const FilterComponent = ({
  filters,
  setFilters,
  data,
  applyFilters,
  setFilterShow,
}) => {
  const today = dayjs().startOf("day");

  // Predefined date ranges
  const predefinedRanges = [
    { label: "Today", startDate: today, endDate: today },
    {
      label: "Yesterday",
      startDate: today.subtract(1, "day"),
      endDate: today.subtract(1, "day"),
    },
    {
      label: "Last 7 Days",
      startDate: today.subtract(7, "day"),
      endDate: today,
    },
    {
      label: "Last Month",
      startDate: today.subtract(1, "month").startOf("month"),
      endDate: today.subtract(1, "month").endOf("month"),
    },
    { label: "Custom", startDate: null, endDate: null },
  ];

  const [filtersData, setFiltersData] = useState({
    selectedRange: predefinedRanges[2], // Default to "Last 7 Days"
    customStartDate: predefinedRanges[2].startDate,
    customEndDate: predefinedRanges[2].endDate,
    agentName: "",
  });

  useEffect(() => {
    if (filters && filters.startDate && filters.endDate) {
      const selectedRange =
        predefinedRanges.find((range) => {
          // Format dates to YYYY-MM-DD for comparison
          const rangeStart = range.startDate?.format("YYYY-MM-DD");
          const rangeEnd = range.endDate?.format("YYYY-MM-DD");
          const filterStart = dayjs(filters.startDate).format("YYYY-MM-DD");
          const filterEnd = dayjs(filters.endDate).format("YYYY-MM-DD");

          return rangeStart === filterStart && rangeEnd === filterEnd;
        }) || { label: "Custom" };

      setFiltersData({
        selectedRange,
        customStartDate: dayjs(filters.startDate),
        customEndDate: dayjs(filters.endDate),
        agentName: filters.agentName || "",
      });
    }
  }, [filters]);



  const agentNameOptions = useMemo(() => {
    return (data || []).map((agent) => ({
      label: agent.agent_name, // Use agent_name for label
      value: agent.agent_name, // Use agent_name for value
    }));
  }, [data]);

  const handleInputChange = (key, value) => {
    setFiltersData((prevFilters) => {
      const updatedFilters = { ...prevFilters, [key]: value };

      if (key === "selectedRange") {
        if (value.label !== "Custom") {
          updatedFilters.customStartDate = value.startDate;
          updatedFilters.customEndDate = value.endDate;
        }
      }
      return updatedFilters;
    });
  };

  const handleCustomDateChange = (key, value) => {
    setFiltersData((prevFilters) => ({
      ...prevFilters,
      [key]: dayjs(value),
    }));
  };

  const handleApply = () => {
    const { selectedRange, customStartDate, customEndDate } = filtersData;

    const startDate =
      selectedRange.label === "Custom"
        ? customStartDate && customStartDate.isValid()
          ? customStartDate.format("YYYY-MM-DD")
          : null
        : selectedRange.startDate
          ? selectedRange.startDate.format("YYYY-MM-DD")
          : null;

    const endDate =
      selectedRange.label === "Custom"
        ? customEndDate && customEndDate.isValid()
          ? customEndDate.format("YYYY-MM-DD")
          : null
        : selectedRange.endDate
          ? selectedRange.endDate.format("YYYY-MM-DD")
          : null;

    setFilters({
      startDate,
      endDate,
      agentName: filtersData.agentName,
    });

    setFilterShow(false);
  };

  const handleClear = () => {
    const defaultRange = predefinedRanges[2]; // "Last 7 Days"
    setFilters({
      startDate: defaultRange.startDate.format("YYYY-MM-DD"),
      endDate: defaultRange.endDate.format("YYYY-MM-DD"),
      agentName: "",
    });

    setFiltersData({
      selectedRange: defaultRange,
      customStartDate: defaultRange.startDate,
      customEndDate: defaultRange.endDate,
      agentName: "",
    });

    setFilterShow(false);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f0f4f8",
        padding: 1,
        borderRadius: 2,
        boxShadow: 3,
        width: "100%",
      }}
    >
      <Grid container spacing={2}>
        {/* Predefined Ranges */}
        <Grid item xs={12} sm={2}>
          <Select
            value={filtersData.selectedRange.label}
            onChange={(e) =>
              handleInputChange(
                "selectedRange",
                predefinedRanges.find((range) => range.label === e.target.value)
              )
            }
            fullWidth
            size="small"
          >
            {predefinedRanges.map((range) => (
              <MenuItem key={range.label} value={range.label}>
                {range.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Date Pickers */}
        <Grid item xs={12} sm={2}>
          <TextField
            label="Start Date"
            type="date"
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            value={
              filtersData.customStartDate
                ? filtersData.customStartDate.format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) =>
              handleCustomDateChange("customStartDate", e.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            label="End Date"
            type="date"
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            value={
              filtersData.customEndDate
                ? filtersData.customEndDate.format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) =>
              handleCustomDateChange("customEndDate", e.target.value)
            }
          />
        </Grid>

        {/* Agent Name Autocomplete */}
        {localStorage.getItem("empRole") === "301" && (<Grid item xs={12} sm={2}>
          <Autocomplete
            options={agentNameOptions}
            value={
              agentNameOptions.find(
                (option) => option.value === filtersData.agentName
              ) || null
            }
            onChange={(event, newValue) =>
              handleInputChange("agentName", newValue?.value || "")
            }
            renderInput={(params) => (
              <TextField {...params} label="Agent Name" size="small" fullWidth />
            )}
            getOptionLabel={(option) => option.label || ""}
            isOptionEqualToValue={(option, value) => option.value === value}
          />
        </Grid>)}

        {/* Action Buttons */}
        <Grid item xs={12} sm={3}>
          <Box sx={{ display: "flex" }}>
            <Button
              onClick={handleClear}
              variant="outlined"
              color="secondary"
              sx={{ marginRight: 2 }}
            >
              Clear
            </Button>
            <Button onClick={handleApply} variant="contained" color="primary">
              Apply
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FilterComponent;
